import { useMemo, type ReactNode } from 'react';

import classnames from 'clsx';
import { get } from 'lodash-es';

import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalCloseButton } from 'ui-components/components/modal/modal-close-button';
import { FancyStateIcon } from 'ui-components/components/fancy-state-icon';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Button } from 'ui-components/components/button';
import type { ButtonThemeValues, ButtonClickHandler } from 'ui-components/components/button/button-types';
import { v4 } from 'uuid';
import type { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { emptyArray } from 'ui-components/default-props/empty';

import style from './error-modal.module.css';
import { noop } from 'ui-components/default-props/noop';

enum ModalState {
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export type ErrorModalButton = {
  onClick?: ButtonClickHandler;
  theme?: ButtonThemeValues;
  children: ReactNode;
  href?: string;
};

export type ErrorModalProps = {
  modalFrameworkMethods: typeof ModalFrameworkMethodsPropType;
  state?: ModalState;
  title: ReactNode;
  className?: string;
  message?: ReactNode;
  buttons?: readonly ErrorModalButton[];
};

const stateToDisplayInformationMap = {
  [ModalState.Error]: {
    stateClassName: '',
  },
  [ModalState.Warning]: {
    stateClassName: style.warning,
  },
  [ModalState.Info]: {
    stateClassName: style.info,
  },
};

/**
 * ErrorModal component displays error, warning, or info messages in a modal dialog
 * @example
 * ```tsx
 * <ErrorModal
 *   modalFrameworkMethods={{ closeModal: handleClose }}
 *   state="error"
 *   title="Error occurred"
 *   message="Something went wrong"
 *   buttons={[
 *     { children: 'Cancel', theme: 'tertiary' },
 *     { children: 'Retry', theme: 'primary', onClick: handleRetry }
 *   ]}
 * />
 * ```
 * @param {ErrorModalProps} props - Component props
 * @returns {JSX.Element} ErrorModal component
 */
export const ErrorModal = ({
  modalFrameworkMethods,
  state = ModalState.Error,
  title,
  message,
  buttons = emptyArray,
  className = '',
}: ErrorModalProps) => {
  const modalHeaderId = useMemo(() => v4(), []);
  // @ts-expect-error Interface not defined
  const { closeModal } = modalFrameworkMethods;
  const displayInformation = stateToDisplayInformationMap[state] || stateToDisplayInformationMap[ModalState.Error];
  const { stateClassName } = displayInformation;

  return (
    <Modal className={classnames(style['error-modal'], className, stateClassName)} modalHeaderId={modalHeaderId}>
      <ModalBody className={style['error-modal-body']}>
        <div className={style['title-row']}>
          <div className={style['state-icon-wrapper']}>
            <FancyStateIcon state={state} />
          </div>
          <div className={style['title-text']} id={modalHeaderId}>
            {title}
          </div>
          <div className={style['close-button-wrapper']}>
            <ModalCloseButton closeModal={() => closeModal(true)} />
          </div>
        </div>
        {message && <div className={style.message}>{message}</div>}
      </ModalBody>
      {buttons.length > 0 && (
        <ModalFooter>
          {buttons.map((buttonProps, index) => (
            <Button
              theme={THEME_CONSTANTS.tertiary}
              {...buttonProps}
              key={index}
              className={style['footer-button']}
              onClick={(event) => {
                closeModal(false);
                const onClick = get(buttonProps, 'onClick', noop);
                onClick(event);
              }}
            />
          ))}
        </ModalFooter>
      )}
    </Modal>
  );
};
